import { isArray, isEmpty } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import AuthService from '../../services/authService';
import UserService from '../../services/userService';
import { useQuery } from '../../services/utils';
import { useSelector, useDispatch } from 'react-redux';
import { updateAuthUser } from '../../store/actions/auth';
import FormSelect from './FormSelect';
import Header from '../../components/Header';
import { Api } from '../../services/api';
import CompletedRegistration from './CompletedRegistration';
import { Appointment } from '../../revamp/types/AppointmentTypes';
import { MedicalHistory } from '../../revamp/types/PatientFormTypes';
import _ from 'lodash';
import { User } from 'src/revamp/types/UserTypes';
import { PaperWorkPdfGenerator } from './PaperWorkPdfGenerator';

const VALID_STEPS = [
  'details',
  'sleep-update',
  'social-history',
  'medications',
  'provider',
  'complete'
]

type Step = typeof VALID_STEPS[number];

const FollowUpPage = (props: any) => {
  const { user } = AuthService.getAuth();
  const query = useQuery();
  let step = query.get('step') || user?.onboardingProgress;
  const view = query.get('view');
  if (!step || !VALID_STEPS.includes(step)) step = 'details';

  const history = useHistory();
  const dispatch = useDispatch();

  const [showVerify, setShowVerify] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [email, setEmail] = useState('');
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>();
  const [fetchingHistory, setFetchingHistory] = useState(true);
  const [fetchingLogo, setFetchingLogo] = useState(false);
  const [partner, setPartner] = useState({});
  const [base64, setBase64] = useState('');
  const [nearestApt, setNearestApt] = useState<Appointment>();

  const getNextStep = (currStep: Step) => {
    const nextSteps: Record<Step, Step> = {
      'details': 'sleep-update',
      'sleep-update': 'social-history',
      'social-history': 'medications',
      'medications': 'provider',
      'provider': 'complete',
      'complete': 'complete',
    }
    return nextSteps[currStep] || 'details';
  }

  useEffect(() => {
    getPartnerLogo();
  }, []);


  useEffect(() => {
    if (!nearestApt) {
      getAppointment();
    }
  }, [nearestApt]);

  useEffect(() => {
    if (user?.id && !medicalHistory) getMedicalHistory();
    else if (fetchingHistory) setFetchingHistory(false);
  }, [step]);

  const getAppointment = () => {
    UserService.getAppointments({ types: ['upcoming', 'ongoing'] })
      .then((res) => {
        const apts = res.data;
        if (apts.length) {
          const nearestApt = apts
            .sort((a, b) => moment(b.startTime).diff(moment(a.startTime)))
            .pop();
          setNearestApt(nearestApt);
        } 
      })
      .catch((err) => toast.error(err));
  };

  const updateHistory = (formData: MedicalHistory, aptData?: Appointment) => {
    setMedicalHistory({
      ...medicalHistory,
      ...formData,
    })

    // NOTE: apt id used to determine value of medicalId
    // Below fixes the issue with multiple formData records for follow up forms
    if (aptData) setNearestApt({ 
      ...nearestApt, 
      ...aptData 
    });
  }


  const getPartnerLogo = () => {
    const refId = query.get('refId')
    if (refId) {
      setFetchingLogo(true);
      Api.get(`/referralUsers?refId=${refId}`)
        .then(async (res) => {
          let data = res.data[0];
          const logoBuffer = data?.logoBuffer?.data;
          const base64 = btoa(
            new Uint8Array(logoBuffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              '',
            ),
          );
          setPartner(res.data[0])
          setBase64('data:;base64,' + base64)
        })
        .finally(() => setFetchingLogo(false));
    }
  };

  const getMedicalHistory = () => {
    setFetchingHistory(true);
    UserService.getMedicalHistoryByUserId(user.id)
      .then((res) => {
        const medicalHistories = res?.data
        const latestMedicalHistory = medicalHistories.pop()
        if (latestMedicalHistory) setMedicalHistory(latestMedicalHistory)
      })
      .catch((e) => toast.error('Unable to retrieve medical history.'))
      .finally(() => setFetchingHistory(false));
  };

  const generatePdf = () => {
    const generator = new PaperWorkPdfGenerator(user, medicalHistory);
    generator.draw();
    const file = new File(
      [generator.doc.output('blob')],
      `Follow-up Patient Form.pdf`,
      { type: 'application/pdf' },
    );

    const body = {
      category: 'OTHER',
      patientId: user.id,
      usedFor: 'patient form',
    };

    UserService.createForm(file, body)
      .catch(err => toast.error(err.message));
  }

  const onContinue = (step: string, data: any) => {
    if (!isEmpty(data)) {
      AuthService.updateUserInfo(data);
      dispatch(updateAuthUser(data));
    }

    if (props.onSave) return props.onSave();
    else if (step === 'complete') {
      generatePdf();
      setShowComplete(true);
    }
    else return history.push(`/followUp?step=${step}`);
  };

  if (fetchingHistory || fetchingLogo) return <div>Loading...</div>;

  return (
    <Fragment>
      {showComplete ? <CompletedRegistration /> : (
        <Fragment>
          <FormSelect
            apptId={nearestApt?.id}
            medicalHistory={medicalHistory}
            medicalId={nearestApt?.medicalId || ''}
            step={step}
            updateHistory={updateHistory}
            onContinue={(user?: User) => onContinue(getNextStep(step), user)}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

export default FollowUpPage;
